import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Shield, Users, DollarSign, TrendingUp, FileText, Zap, Search, ArrowLeft, Loader2, ChevronLeft, ChevronRight, Cpu, Activity, Route, BookOpen, Target, Clock, Download, Mail, CheckCircle, XCircle, AlertCircle, RefreshCw, Bell } from "lucide-react";
import axios from "axios";
import RecruiterVerificationPanel from "@/components/admin/RecruiterVerificationPanel";
import AdminShell from "@/components/admin/AdminShell";
import AdminOverview from "@/components/admin/AdminOverview";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function reportingRangeParams(range) {
  if (range === "all") return { all_time: true };
  const end = new Date();
  const start = new Date(end);
  if (range === "today") start.setHours(0, 0, 0, 0);
  if (range === "7d") start.setDate(start.getDate() - 6);
  if (range === "30d") start.setDate(start.getDate() - 29);
  if (range === "quarter") {
    start.setMonth(Math.floor(start.getMonth() / 3) * 3, 1);
    start.setHours(0, 0, 0, 0);
  }
  const localDate = (value) => `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
  return { start_date: localDate(start), end_date: localDate(end) };
}

const ADMIN_TABS = ["overview", "recruiters", "users", "payments", "transactions", "ai-costs", "skill-paths", "contact", "resumes", "emails", "email-campaigns"];

// Multi-currency payments: render every transaction in ITS OWN currency and tag
// its market. Legacy rows (no market/currency) are India/INR.
const CUR_SYM = { inr: "\u20B9", usd: "$", gbp: "\u00A3", cad: "C$", aud: "A$" };
// NOTE: country-flag emoji do NOT render on Windows (Segoe UI Emoji has no
// flags) — use a styled code chip that renders identically everywhere.
const MKT_CHIP_COLOR = { IN: "#FF6B35", US: "#2832C2", UK: "#7C3AED", CA: "#16A34A", AU: "#0EA5E9" };
const MarketChip = ({ code, small }) => (
  <span
    className={`inline-flex items-center justify-center font-bold rounded ${small ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-0.5"}`}
    style={{ background: `${MKT_CHIP_COLOR[code] || "#6b7280"}18`, color: MKT_CHIP_COLOR[code] || "#6b7280", letterSpacing: "0.04em" }}
  >
    {code}
  </span>
);
const txnMarket = (t) => t.market || ({ inr: "IN", usd: "US", gbp: "UK", cad: "CA", aud: "AU" }[(t.currency || "inr").toLowerCase()] || "IN");
const fmtTxnAmount = (t) => `${CUR_SYM[(t.currency || "inr").toLowerCase()] || "\u20B9"}${(t.amount || 0).toLocaleString()}`;

function StatCard({ icon: Icon, label, value, color, prefix = "" }) {
  return (
    <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
          <div>
            <p className="text-gray-500 text-xs">{label}</p>
            <p className="font-['JetBrains_Mono'] text-lg font-bold text-[#0A1128]">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FunnelBar({ label, value, total, color }) {
  const pct = total > 0 ? (value / total * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-gray-500">{label}</span>
        <span className="font-['JetBrains_Mono'] text-[#0A1128]">{value} <span className="text-gray-300">({pct.toFixed(1)}%)</span></span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(pct, 2)}%`, background: color }} />
      </div>
    </div>
  );
}

function NewTemplateForm({ onCreated, getHeaders, API }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("announcements");
  const [subject, setSubject] = useState("");
  const [htmlBody, setHtmlBody] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name || !subject || !htmlBody) { toast.error("Please fill all fields"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/email-templates`, {
        name, category, subject, html_body: htmlBody, text_body: htmlBody.replace(/<[^>]*>/g, '')
      }, { headers: getHeaders() });
      toast.success("Template saved!");
      setName(""); setSubject(""); setHtmlBody("");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <input
          value={name} onChange={e => setName(e.target.value)}
          placeholder="Template name..."
          className="px-3 py-2 border-2 border-[#e5e7eb] rounded-lg text-gray-900 bg-white text-sm"
        />
        <select
          value={category} onChange={e => setCategory(e.target.value)}
          className="px-3 py-2 border-2 border-[#e5e7eb] rounded-lg text-gray-700 bg-white text-sm"
        >
          <option value="announcements">Announcements</option>
          <option value="job_opportunities">Job Opportunities</option>
          <option value="product_updates">Product Updates</option>
          <option value="re_engagement">Re-Engagement</option>
        </select>
      </div>
      <input
        value={subject} onChange={e => setSubject(e.target.value)}
        placeholder="Email subject..."
        className="w-full px-3 py-2 border-2 border-[#e5e7eb] rounded-lg text-gray-900 bg-white text-sm"
      />
      <textarea
        value={htmlBody} onChange={e => setHtmlBody(e.target.value)}
        placeholder="Email body (HTML supported). Use {name} and {email} as placeholders..."
        rows={6}
        className="w-full px-3 py-2 border-2 border-[#e5e7eb] rounded-lg text-gray-900 bg-white text-sm font-mono"
      />
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full px-4 py-2 bg-[#2832C2] text-white rounded-lg hover:bg-[#1e2590] disabled:opacity-50 text-sm"
      >
        {saving ? "Saving..." : "Save Template"}
      </button>
    </div>
  );
}

export default function AdminPage() {
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [userPages, setUserPages] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [dateRange, setDateRange] = useState("today");
  const [reporting, setReporting] = useState(null);
  const [reportingError, setReportingError] = useState("");
  // Tab persists in the URL (?tab=payments) so a refresh stays on the same tab
  // and admin views are shareable/bookmarkable. Invalid values fall back safely.
  const [searchParams, setSearchParams] = useSearchParams();
  const _urlTab = searchParams.get("tab");
  const [tab, setTabState] = useState(ADMIN_TABS.includes(_urlTab) ? _urlTab : "overview");
  const setTab = (t) => { setTabState(t); setSearchParams({ tab: t }, { replace: true }); };
  const [aiCosts, setAiCosts] = useState(null);
  const [skillPathStats, setSkillPathStats] = useState(null);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [contactTotal, setContactTotal] = useState(0);
  const [contactPage, setContactPage] = useState(1);
  const [contactPages, setContactPages] = useState(1);
  const [contactFilter, setContactFilter] = useState("all");
  const [resumes, setResumes] = useState([]);
  const [resumeTotal, setResumeTotal] = useState(0);
  const [resumePage, setResumePage] = useState(1);
  const [resumePages, setResumePages] = useState(1);
  const [resumeSearch, setResumeSearch] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsTotal, setEmailLogsTotal] = useState(0);
  const [emailLogsPage, setEmailLogsPage] = useState(1);
  const [emailLogsPages, setEmailLogsPages] = useState(1);
  const [emailLogsSearch, setEmailLogsSearch] = useState("");
  const [emailLogsType, setEmailLogsType] = useState("");
  const [emailLogsStatus, setEmailLogsStatus] = useState("");
  const [emailLogsSummary, setEmailLogsSummary] = useState(null);
  const [triggeringNudge, setTriggeringNudge] = useState(false);

  // Payments tab state
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsPages, setPaymentsPages] = useState(1);
  const [paymentsSearch, setPaymentsSearch] = useState("");
  const [paymentsProduct, setPaymentsProduct] = useState("");
  // Default to "" (All Statuses) so admins see Pending rows + Mark Paid actions
  // immediately when opening the Payments tab, instead of starting on Paid-only.
  const [paymentsStatus, setPaymentsStatus] = useState("");
  const [paymentsSummary, setPaymentsSummary] = useState(null);
  const [paymentsMarket, setPaymentsMarket] = useState(""); // "" = all markets
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [markingPaidId, setMarkingPaidId] = useState("");

  // Email Campaigns State
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsPages, setContactsPages] = useState(1);
  const [contactsSearch, setContactsSearch] = useState("");
  const [contactsRole, setContactsRole] = useState("");
  const [contactsSource, setContactsSource] = useState("");
  const [contactsStatus, setContactsStatus] = useState("");
  const [contactStats, setContactStats] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsTotal, setCampaignsTotal] = useState(0);
  const [campaignsPage, setCampaignsPage] = useState(1);
  const [showComposer, setShowComposer] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [testEmailAddr, setTestEmailAddr] = useState("");
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);
  
  const navigate = useNavigate();
  const { getHeaders, user } = useAuth();

  useEffect(() => {
    if (!user) { navigate("/auth?return=/admin"); return; }
    fetchDashboard();
  }, [user, dateRange]); // eslint-disable-line

  useEffect(() => {
    if (user) fetchReporting();
  }, [user, dateRange]); // eslint-disable-line

  useEffect(() => {
    if (tab === "users") fetchUsers();
    if (tab === "ai-costs" && !aiCosts) fetchAiCosts();
    if (tab === "skill-paths" && !skillPathStats) fetchSkillPathStats();
    if (tab === "contact") fetchContactSubmissions();
    if (tab === "resumes") fetchResumes();
    if (tab === "emails") fetchEmailLogs();
    if (tab === "payments") fetchPayments();
    if (tab === "email-campaigns") {
      fetchCollectedContacts();
      if (!contactStats) fetchContactStats();
      if (emailTemplates.length === 0) fetchEmailTemplates();
    }
  }, [tab, userPage, search, contactPage, contactFilter, resumePage, resumeSearch, emailLogsPage, emailLogsSearch, emailLogsType, emailLogsStatus, contactsPage, contactsSearch, contactsRole, contactsSource, contactsStatus, paymentsPage, paymentsSearch, paymentsProduct, paymentsStatus, paymentsMarket]); // eslint-disable-line

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`, { headers: getHeaders(), params: reportingRangeParams(dateRange) });
      setData(res.data);
      setDashboardError("");
      setRefreshedAt(new Date());
      return true;
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      } else {
        setDashboardError(err.response?.data?.detail || "The admin dashboard could not be loaded.");
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const fetchReporting = async () => {
    const params = reportingRangeParams(dateRange);
    try {
      const requests = await Promise.allSettled([
        axios.get(`${API}/admin/reporting/overview`, { headers: getHeaders(), params }),
        axios.get(`${API}/admin/reporting/operations`, { headers: getHeaders(), params }),
        axios.get(`${API}/admin/reporting/health`, { headers: getHeaders(), params }),
      ]);
      const [overview, operations, health] = requests;
      if (overview.status !== "fulfilled") throw overview.reason;
      setReporting({
        ...overview.value.data,
        operations: operations.status === "fulfilled" ? operations.value.data : null,
        health: health.status === "fulfilled" ? health.value.data : null,
      });
      setReportingError(operations.status === "fulfilled" ? "" : "Some operational reporting is temporarily unavailable.");
    } catch (err) {
      setReporting(null);
      setReportingError(err.response?.data?.detail || "Advanced reporting is temporarily unavailable.");
    }
  };

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams({ page: userPage.toString() });
      if (search) params.append("search", search);
      const res = await axios.get(`${API}/admin/users?${params}`, { headers: getHeaders() });
      setUsers(res.data.users);
      setUserTotal(res.data.total);
      setUserPages(res.data.pages);
    } catch (e) { /* ok */ }
  };

  const fetchPayments = async () => {
    setPaymentsLoading(true);
    try {
      const params = new URLSearchParams({ page: paymentsPage.toString(), page_size: "25" });
      if (paymentsSearch) params.append("search", paymentsSearch);
      if (paymentsProduct) params.append("product", paymentsProduct);
      if (paymentsStatus) params.append("status", paymentsStatus);
      if (paymentsMarket) params.append("market", paymentsMarket);
      const res = await axios.get(`${API}/admin/payments?${params}`, { headers: getHeaders() });
      setPayments(res.data.transactions);
      setPaymentsTotal(res.data.total);
      setPaymentsPages(res.data.pages);
      setPaymentsSummary(res.data.summary);
    } catch (e) { /* ok */ } finally {
      setPaymentsLoading(false);
    }
  };

  const markPaymentPaid = async (txn) => {
    const buyer = txn.user_email || txn.guest_email || "this transaction";
    const ok = window.confirm(
      `Mark this payment as PAID for ${buyer}?\n\n` +
      `This will:\n` +
      `• Flip the transaction to "paid"\n` +
      `• Create a user account if one doesn't exist\n` +
      `• Activate entitlements (Full Career OS / resume report / credits)\n` +
      `• Run the AI pipeline + email the optimized resume (resume_analyse only)\n` +
      `• Email the buyer a forgot-password link so they can set a password\n\n` +
      `Use this only when payment is confirmed outside Stripe.`
    );
    if (!ok) return;
    setMarkingPaidId(txn.id);
    try {
      const res = await axios.post(
        `${API}/admin/payments/${txn.id}/mark-paid`,
        {},
        { headers: getHeaders() }
      );
      if (res.data?.already_paid) {
        toast.info(`Already paid — re-synced ${res.data.user_email}`);
      } else {
        toast.success(
          res.data?.is_new_user
            ? `Marked paid + new user created (${res.data.user_email})`
            : `Marked paid for ${res.data.user_email}`
        );
      }
      await fetchPayments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark payment as paid");
    } finally {
      setMarkingPaidId("");
    }
  };

  const fetchAiCosts = async () => {
    try {
      const res = await axios.get(`${API}/admin/ai-costs`, { headers: getHeaders() });
      setAiCosts(res.data);
    } catch (e) { /* ok */ }
  };

  const fetchSkillPathStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/skill-path-stats`, { headers: getHeaders() });
      setSkillPathStats(res.data);
    } catch (e) { /* ok */ }
  };

  const fetchContactSubmissions = async () => {
    try {
      const params = new URLSearchParams({ 
        page: contactPage.toString(),
        status: contactFilter
      });
      const res = await axios.get(`${API}/admin/contact-submissions?${params}`, { headers: getHeaders() });
      setContactSubmissions(res.data.submissions);
      setContactTotal(res.data.total);
      setContactPages(res.data.total_pages);
    } catch (e) { /* ok */ }
  };

  const updateContactStatus = async (submissionId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/contact-submissions/${submissionId}/status?status=${newStatus}`, {}, { headers: getHeaders() });
      toast.success("Status updated");
      fetchContactSubmissions();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };


  const fetchResumes = async () => {
    try {
      const res = await axios.get(`${API}/admin/resumes`, { 
        params: { page: resumePage, search: resumeSearch },
        headers: getHeaders() 
      });
      setResumes(res.data.resumes || []);
      setResumeTotal(res.data.total || 0);
      setResumePages(res.data.total_pages || 1);
    } catch (e) {
      toast.error("Failed to fetch resumes");
    }
  };

  const downloadResumeText = async (resumeId, filename) => {
    try {
      const res = await axios.get(`${API}/admin/resumes/${resumeId}/download`, {
        headers: getHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'resume.txt');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Resume downloaded");
    } catch (e) {
      toast.error("Failed to download resume");
    }
  };


  const fetchEmailLogs = async () => {
    try {
      const params = new URLSearchParams({ page: emailLogsPage.toString(), page_size: "50" });
      if (emailLogsSearch) params.append("search", emailLogsSearch);
      if (emailLogsType)   params.append("email_type", emailLogsType);
      if (emailLogsStatus) params.append("status", emailLogsStatus);
      const res = await axios.get(`${API}/admin/email-logs?${params}`, { headers: getHeaders() });
      setEmailLogs(res.data.logs || []);
      setEmailLogsTotal(res.data.total || 0);
      setEmailLogsPages(res.data.total_pages || 1);
      setEmailLogsSummary(res.data.summary || null);
    } catch (e) {
      toast.error("Failed to fetch email logs");
    }
  };

  const triggerNudgeEmails = async () => {
    setTriggeringNudge(true);
    try {
      await axios.post(`${API}/admin/nudge/trigger`, {}, { headers: getHeaders() });
      toast.success("Nudge job triggered — check logs in a moment");
      setTimeout(fetchEmailLogs, 2000);
    } catch (e) {
      toast.error("Failed to trigger nudge job");
    } finally {
      setTriggeringNudge(false);
    }
  };

  const syncResumeContacts = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/resumes/sync`, {}, {
        headers: getHeaders()
      });
      
      const { total_resumes, processed, emails_found, phones_found, errors } = res.data;
      
      let message = `Sync Complete!\n\n`;
      message += `✅ Processed: ${processed}/${total_resumes} resumes\n`;
      message += `📧 Emails found: ${emails_found}\n`;
      message += `📞 Phones found: ${phones_found}`;
      
      if (errors && errors.length > 0) {
        message += `\n\n⚠️ ${errors.length} errors occurred`;
      }
      
      toast.success(message, { duration: 5000 });
      
      // Refresh the resumes list
      fetchResumes();
    } catch (e) {
      toast.error("Failed to sync resume contacts");
    } finally {
      setSyncing(false);
    }
  };


  // Email Campaigns Functions
  const fetchCollectedContacts = async () => {
    try {
      const params = new URLSearchParams({
        page: contactsPage,
        page_size: 50,
        ...(contactsRole && { role: contactsRole }),
        ...(contactsSource && { source: contactsSource }),
        ...(contactsStatus && { status: contactsStatus }),
        ...(contactsSearch && { search: contactsSearch })
      });
      const res = await axios.get(`${API}/admin/collected-contacts?${params}`, { headers: getHeaders() });
      setContacts(res.data.contacts);
      setContactsTotal(res.data.total);
      setContactsPages(res.data.total_pages);
    } catch (err) {
      toast.error("Failed to fetch contacts");
    }
  };

  const fetchContactStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/collected-contacts/stats`, { headers: getHeaders() });
      setContactStats(res.data);
    } catch (err) {
      toast.error("Failed to fetch contact stats");
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-templates`, { headers: getHeaders() });
      setEmailTemplates(res.data.templates);
    } catch (err) {
      toast.error("Failed to fetch email templates");
    }
  };

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-campaigns?page=${campaignsPage}&page_size=20`, { headers: getHeaders() });
      setCampaigns(res.data.campaigns);
      setCampaignsTotal(res.data.total);
    } catch (err) {
      toast.error("Failed to fetch campaigns");
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddr) { toast.error("Enter a recipient email"); return; }
    setTestEmailSending(true);
    setTestEmailResult(null);
    try {
      const res = await axios.post(`${API}/admin/test-email`, { to_email: testEmailAddr }, { headers: getHeaders() });
      setTestEmailResult({ success: true, message: res.data.message });
      toast.success("Test email sent!");
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to send test email";
      setTestEmailResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setTestEmailSending(false);
    }
  };

  const handleSendBulkEmail = async () => {
    if (!emailSubject || !emailBody) {
      toast.error("Please fill in subject and body");
      return;
    }

    setSendingEmail(true);
    try {
      const payload = {
        contact_ids: selectedContacts,
        filters: {},
        subject: emailSubject,
        html_body: emailBody,
        text_body: emailBody.replace(/<[^>]*>/g, ''), // Strip HTML for text version
        template_id: selectedTemplate
      };

      // If no contacts selected, use filters
      if (selectedContacts.length === 0) {
        payload.filters = {
          ...(contactsRole && { role: contactsRole }),
          ...(contactsSource && { source: contactsSource }),
          ...(contactsStatus && { status: contactsStatus })
        };
      }

      const res = await axios.post(`${API}/admin/send-bulk-email`, payload, { headers: getHeaders() });
      
      toast.success(`✅ Email sent to ${res.data.sent_count} contacts! (${res.data.failed_count} failed)`);
      setShowComposer(false);
      setEmailSubject("");
      setEmailBody("");
      setSelectedContacts([]);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send emails");
    } finally {
      setSendingEmail(false);
    }
  };

  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(c => c.id));
    }
  };




  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const dashboardOk = await fetchDashboard();
      if (!dashboardOk) {
        toast.error("Could not refresh the admin dashboard");
        return;
      }
      await fetchReporting();
      if (tab === "users") await fetchUsers();
      if (tab === "payments") await fetchPayments();
      if (tab === "ai-costs") await fetchAiCosts();
      if (tab === "skill-paths") await fetchSkillPathStats();
      if (tab === "contact") await fetchContactSubmissions();
      if (tab === "resumes") await fetchResumes();
      if (tab === "emails") await fetchEmailLogs();
      if (tab === "email-campaigns") {
        await fetchCollectedContacts();
        await fetchContactStats();
        await fetchEmailTemplates();
      }
      toast.success("Admin data refreshed");
    } finally {
      setRefreshing(false);
    }
  };

  const downloadOriginalResume = async (resumeId, filename) => {
    try {
      const res = await axios.get(`${API}/admin/resumes/${resumeId}/file`, {
        headers: getHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename || "resume");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Original resume downloaded");
    } catch (e) {
      toast.error(e.response?.status === 404 ? "Original file is not available for this upload" : "Failed to download resume file");
    }
  };

  if (loading) return <div className="admin-loading-screen ap-root" role="status"><div className="admin-loading-card"><Loader2 className="h-7 w-7 animate-spin text-violet-600" /><span>Preparing your operations workspace...</span></div></div>;

  if (!data) return <div className="admin-loading-screen ap-root" data-testid="admin-error-state"><div className="admin-loading-card admin-loading-card--error"><Shield className="h-7 w-7 text-rose-500" /><div><strong>Admin dashboard unavailable</strong><p>{dashboardError || "No dashboard data is available yet."}</p><button type="button" className="admin-retry-button" onClick={handleRefresh}>Try again</button></div></div></div>;

  const { stats = {}, funnel = {}, recent_users = [], recent_transactions = [] } = data;

  return (
    <AdminShell
      tab={tab}
      setTab={setTab}
      navigate={navigate}
      onRefresh={handleRefresh}
      refreshing={refreshing}
      refreshedAt={refreshedAt}
      dateRange={dateRange}
      setDateRange={setDateRange}
    >
      <div className="admin-content" data-testid="admin-page">
      <div className="max-w-6xl mx-auto space-y-6">
        {tab === "overview" && <AdminOverview data={{ ...data, reporting }} dateRange={dateRange} reportingError={reportingError} />}

        {false && tab === "overview" && (
          <>
            {/* Revenue Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={DollarSign} label="Revenue (India)" value={stats.total_revenue} color="#16a34a" prefix={"\u20B9"} />
              <StatCard icon={Users} label="Total Users" value={stats.total_users} color="#2832C2" />
              <StatCard icon={FileText} label="Resumes Uploaded" value={stats.total_resumes} color="#2832C2" />
              <StatCard icon={Zap} label="Full Career OS Users" value={stats.season_pass_users} color="#2832C2" />
            </div>

            {/* Revenue breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard icon={FileText} label="Roast Revenue" value={stats.roast_revenue} color="#2832C2" prefix={"\u20B9"} />
              <StatCard icon={Zap} label="Full Career OS Revenue" value={stats.pass_revenue} color="#2832C2" prefix={"\u20B9"} />
              <StatCard icon={TrendingUp} label="Credit Revenue" value={stats.credit_revenue} color="#2832C2" prefix={"\u20B9"} />
              <StatCard icon={Cpu} label="AI Spend (USD)" value={stats.total_ai_cost_usd || 0} color="#ef4444" prefix="$" />
            </div>

            {/* Funnel */}
            <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]" data-testid="funnel-card">
              <CardHeader>
                <CardTitle className="text-[#0A1128] text-lg font-['Chivo']">Conversion Funnel</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FunnelBar label="Users Registered" value={funnel.users_registered} total={funnel.users_registered} color="#2832C2" />
                <FunnelBar label="Resumes Uploaded" value={funnel.resumes_uploaded} total={funnel.users_registered} color="#2832C2" />
                <FunnelBar label="Analyses Triggered" value={funnel.analyses_triggered} total={funnel.users_registered} color="#2832C2" />
                <FunnelBar label="Analyses Paid (₹200)" value={funnel.analyses_paid} total={funnel.users_registered} color="#16a34a" />
                <FunnelBar label="Full Career OS" value={funnel.season_passes} total={funnel.users_registered} color="#eab308" />
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
                <CardHeader><CardTitle className="text-[#0A1128] text-sm font-['Chivo']">Recent Users</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {recent_users?.map((u, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded border border-gray-100">
                        <div>
                          <span className="text-[#0A1128]">{u.name || u.email}</span>
                          <span className="text-gray-500 ml-2">{u.email}</span>
                        </div>
                        {u.has_season_pass && <Badge className="bg-[#2832C2]/20 text-[#2832C2] border-0 text-[10px]">PASS</Badge>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
                <CardHeader><CardTitle className="text-[#0A1128] text-sm font-['Chivo']">Recent Transactions</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {recent_transactions?.map((t, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded border border-gray-100">
                        <div>
                          <Badge className={`border-0 text-[10px] mr-2 ${t.product === 'season_pass' ? 'bg-[#2832C2]/20 text-[#2832C2]' : t.product === 'ai_credits' ? 'bg-[#2832C2]/20 text-[#2832C2]' : 'bg-[#2832C2]/20 text-[#2832C2]'}`}>
                            {t.product || "analyse"}
                          </Badge>
                          <span className="text-gray-500">{t.created_at?.slice(0, 10)}</span>
                        </div>
                        <span className="font-['JetBrains_Mono'] text-[#16a34a]">{fmtTxnAmount(t)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {tab === "recruiters" && (
          <RecruiterVerificationPanel api={API} getHeaders={getHeaders} />
        )}

        {tab === "users" && (
          <>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                <Input
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setUserPage(1); }}
                  className="pl-10 bg-white border-[#e5e7eb] text-[#0A1128] placeholder:text-gray-300 h-10"
                  data-testid="admin-search-input"
                />
              </div>
              <span className="text-gray-500 text-sm self-center whitespace-nowrap">{userTotal} users</span>
            </div>
            <div className="space-y-2">
              {users.map((u, i) => (
                <Card key={i} className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
                  <CardContent className="pt-4 pb-3 flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="text-[#0A1128] text-sm font-medium">{u.name || "—"}</p>
                      <p className="text-gray-500 text-xs">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {u.target_role && <Badge className="bg-gray-100 text-gray-400 border-0 text-[10px]">{u.target_role}</Badge>}
                      {u.has_season_pass && <Badge className="bg-[#2832C2]/20 text-[#2832C2] border-0 text-[10px]">{u.comp_grant ? "FULL CAREER OS · COMP" : "FULL CAREER OS"}</Badge>}
                      {u.free_analysis_pending && <Badge className="bg-green-100 text-green-700 border-0 text-[10px]">🎁 Free Analysis Pending</Badge>}
                      <span className="text-gray-500 text-[10px]">{u.created_at?.slice(0, 10)}</span>
                      <button
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/admin/grant-free-analysis`, { email: u.email }, { headers: getHeaders() });
                            toast.success(`Free analysis granted to ${u.email}`);
                            fetchUsers();
                          } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                        }}
                        title="Grant 1 free ATS analysis"
                        className={`text-xs px-2 py-1 rounded font-medium transition-colors ${u.free_analysis_pending ? "bg-gray-100 text-gray-400 cursor-default" : "bg-[#FF6B35]/10 text-[#FF6B35] hover:bg-[#FF6B35]/20"}`}
                        disabled={u.free_analysis_pending}
                      >
                        {u.free_analysis_pending ? "Granted ✓" : "🎁 Grant Free Analysis"}
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Grant a COMP Season Pass (full Career OS) to ${u.email}?\n\nNo transaction is created — zero impact on revenue reports. For internal/tester/CEO use.`)) return;
                          try {
                            const r = await axios.post(`${API}/admin/grant-season-pass`, { email: u.email }, { headers: getHeaders() });
                            toast.success(r.data?.message || `Season Pass granted to ${u.email}`);
                            fetchUsers();
                          } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                        }}
                        title="Grant a full Season Pass (comp — no revenue impact)"
                        className={`text-xs px-2 py-1 rounded font-medium transition-colors ${u.has_season_pass ? "bg-gray-100 text-gray-400 cursor-default" : "bg-[#2832C2]/10 text-[#2832C2] hover:bg-[#2832C2]/20"}`}
                        disabled={u.has_season_pass}
                      >
                        {u.has_season_pass ? "Pass Active ✓" : "👑 Grant Season Pass"}
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {userPages > 1 && (
              <div className="flex items-center justify-center gap-4">
                <button onClick={() => setUserPage(p => Math.max(1, p - 1))} disabled={userPage <= 1} className="flex items-center gap-1 px-3 py-2 border-2 border-[#e5e7eb] bg-white text-gray-500 rounded text-sm cursor-pointer disabled:opacity-30">
                  <ChevronLeft className="w-4 h-4" /> Prev
                </button>
                <span className="text-gray-500 text-sm font-['JetBrains_Mono']">Page {userPage}/{userPages}</span>
                <button onClick={() => setUserPage(p => Math.min(userPages, p + 1))} disabled={userPage >= userPages} className="flex items-center gap-1 px-3 py-2 border-2 border-[#e5e7eb] bg-white text-gray-500 rounded text-sm cursor-pointer disabled:opacity-30">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}

        {/* ─── PAYMENTS TAB ─────────────────────────────────────────── */}
        {tab === "payments" && (
          <div className="space-y-6">

            {/* Revenue by market — each card in its OWN currency (currencies are
                never summed together). Every supported market shows, zero or
                not; "All markets" restores the combined view. */}
            {paymentsSummary && Object.keys(paymentsSummary.markets || {}).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Revenue by market — click to filter</p>
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={() => { setPaymentsMarket(""); setPaymentsPage(1); }}
                    className={`text-left border-2 rounded-[17.5px] px-5 pt-5 pb-4 transition-all cursor-pointer hover:shadow-md flex-1 min-w-[200px] max-w-[280px] ${
                      paymentsMarket === "" ? "bg-[#2832C2]/5 border-[#2832C2] ring-2 ring-[#2832C2]/20 shadow-md" : "bg-white border-[#e5e7eb]"
                    }`}
                    data-testid="market-card-all"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="inline-flex items-center justify-center font-bold rounded text-[11px] px-2 py-0.5 bg-[#2832C2]/10 text-[#2832C2]" style={{ letterSpacing: "0.04em" }}>ALL</span>
                      <span className="text-gray-500 text-xs font-medium">All markets</span>
                    </div>
                    {/* counts only — revenues in different currencies are never summed */}
                    <p className="font-['JetBrains_Mono'] text-2xl font-bold text-[#2832C2]">{paymentsSummary.total_paid_transactions.toLocaleString()}</p>
                    <p className="text-gray-400 text-[11px] mt-1">paid txns · {paymentsSummary.total_paid_users.toLocaleString()} payers · every market</p>
                  </button>
                  {Object.entries(paymentsSummary.markets)
                    .sort(([ka, ma], [kb, mb]) => (mb.transactions - ma.transactions) || ka.localeCompare(kb))
                    .map(([code, m]) => (
                      <button
                        key={code}
                        onClick={() => { setPaymentsMarket(paymentsMarket === code ? "" : code); setPaymentsPage(1); }}
                        className={`text-left bg-white border-2 rounded-[17.5px] px-5 pt-5 pb-4 transition-all cursor-pointer hover:shadow-md flex-1 min-w-[200px] max-w-[280px] ${
                          paymentsMarket === code ? "border-[#2832C2] ring-2 ring-[#2832C2]/20 shadow-md" : "border-[#e5e7eb]"
                        } ${m.transactions === 0 && paymentsMarket !== code ? "opacity-60" : ""}`}
                        data-testid={`market-card-${code}`}
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <MarketChip code={code} />
                          <span className="text-gray-500 text-xs font-medium">{m.label}</span>
                        </div>
                        <p className={`font-['JetBrains_Mono'] text-2xl font-bold ${m.transactions > 0 ? "text-[#16a34a]" : "text-gray-300"}`}>{m.symbol}{m.revenue.toLocaleString()}</p>
                        <p className="text-gray-400 text-[11px] mt-1">
                          {m.transactions > 0
                            ? <>{m.transactions} txns · {m.payers ?? "—"} payers · avg {m.symbol}{Math.round(m.revenue / m.transactions).toLocaleString()}/txn</>
                            : "no payments captured yet"}
                        </p>
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Cross-market counts (currency-safe) + scoped average */}
            {paymentsSummary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
                  <CardContent className="pt-5 pb-4">
                    <p className="text-gray-500 text-xs mb-1">Paid Users</p>
                    <p className="font-['JetBrains_Mono'] text-2xl font-bold text-[#2832C2]">{paymentsSummary.total_paid_users.toLocaleString()}</p>
                    <p className="text-gray-400 text-[11px] mt-0.5">all markets</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
                  <CardContent className="pt-5 pb-4">
                    <p className="text-gray-500 text-xs mb-1">Paid Transactions</p>
                    <p className="font-['JetBrains_Mono'] text-2xl font-bold text-[#0A1128]">{paymentsSummary.total_paid_transactions.toLocaleString()}</p>
                    <p className="text-gray-400 text-[11px] mt-0.5">all markets</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
                  <CardContent className="pt-5 pb-4">
                    <p className="text-gray-500 text-xs mb-1">Active Markets</p>
                    <p className="font-['JetBrains_Mono'] text-2xl font-bold text-[#7C3AED]">
                      {Object.values(paymentsSummary.markets || {}).filter(m => m.transactions > 0).length}
                      <span className="text-gray-300 text-base font-normal"> / {Object.keys(paymentsSummary.markets || {}).length}</span>
                    </p>
                    <p className="text-gray-400 text-[11px] mt-0.5">with paid revenue</p>
                  </CardContent>
                </Card>
                {(() => {
                  const scope = (paymentsSummary.markets || {})[paymentsMarket] || (paymentsSummary.markets || {}).IN;
                  const scopeCode = (paymentsSummary.markets || {})[paymentsMarket] ? paymentsMarket : "IN";
                  return scope ? (
                    <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
                      <CardContent className="pt-5 pb-4">
                        <p className="text-gray-500 text-xs mb-1">Avg. per Paid User <span className="text-gray-300">·</span> {scopeCode}</p>
                        <p className="font-['JetBrains_Mono'] text-2xl font-bold text-[#FF6B35]">
                          {scope.symbol}{scope.payers > 0 ? Math.round(scope.revenue / scope.payers).toLocaleString() : 0}
                        </p>
                        <p className="text-gray-400 text-[11px] mt-0.5">{scope.label}</p>
                      </CardContent>
                    </Card>
                  ) : null;
                })()}
              </div>
            )}

            {/* Revenue by product, grouped per market (each in its own currency) */}
            {paymentsSummary && Object.keys(paymentsSummary.markets || {}).length > 0 && (
              <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-[#0A1128]">Revenue by Product · per market</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(paymentsSummary.markets)
                    .sort(([ka], [kb]) => (ka === "IN" ? -1 : kb === "IN" ? 1 : ka.localeCompare(kb)))
                    .filter(([code]) => !paymentsMarket || paymentsMarket === code)
                    // zero markets stay off this breakdown unless explicitly selected
                    .filter(([code, m]) => Object.keys(m.by_product).length > 0 || paymentsMarket === code)
                    .map(([code, m]) => (
                      <div key={code} className="flex flex-wrap items-center gap-3">
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-[#0A1128] w-36 shrink-0"><MarketChip code={code} small /> {m.label}</span>
                        {Object.keys(m.by_product).length === 0 && (
                          <span className="text-gray-400 text-xs">no payments captured yet</span>
                        )}
                        {Object.entries(m.by_product).map(([prod, v]) => (
                          <div key={prod} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                            <Badge className="border-0 bg-[#2832C2]/10 text-[#2832C2] text-xs">{prod}</Badge>
                            <span className="font-['JetBrains_Mono'] text-sm font-semibold text-[#0A1128]">{m.symbol}{v.revenue.toLocaleString()}</span>
                            <span className="text-gray-400 text-xs">({v.count})</span>
                          </div>
                        ))}
                      </div>
                    ))}
                </CardContent>
              </Card>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search email, name, session ID..."
                  value={paymentsSearch}
                  onChange={e => { setPaymentsSearch(e.target.value); setPaymentsPage(1); }}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2832C2]/30"
                />
              </div>
              <select
                value={paymentsMarket}
                onChange={e => { setPaymentsMarket(e.target.value); setPaymentsPage(1); }}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-[#0A1128] focus:outline-none focus:ring-2 focus:ring-[#2832C2]/30 bg-white"
                data-testid="payments-market-filter"
              >
                <option value="">All Markets</option>
                <option value="IN">India</option>
                <option value="US">United States</option>
                <option value="UK">United Kingdom</option>
                <option value="CA">Canada</option>
                <option value="AU">Australia</option>
              </select>
              <select
                value={paymentsProduct}
                onChange={e => { setPaymentsProduct(e.target.value); setPaymentsPage(1); }}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-[#0A1128] focus:outline-none focus:ring-2 focus:ring-[#2832C2]/30 bg-white"
              >
                <option value="">All Products</option>
                <option value="resume_analyse">Resume Analyse</option>
                <option value="season_pass">Season Pass</option>
                <option value="ai_credits">AI Credits</option>
                <option value="session_pass">Session Pass</option>
              </select>
              <select
                value={paymentsStatus}
                onChange={e => { setPaymentsStatus(e.target.value); setPaymentsPage(1); }}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-[#0A1128] focus:outline-none focus:ring-2 focus:ring-[#2832C2]/30 bg-white"
              >
                <option value="">All Statuses</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
              <button onClick={fetchPayments} className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <RefreshCw className={`w-4 h-4 text-gray-500 ${paymentsLoading ? "animate-spin" : ""}`} />
              </button>
              <span className="text-sm text-gray-500 ml-auto">{paymentsTotal} transactions</span>
            </div>

            {/* Hint banner: shown when admin is viewing Paid-only — Mark Paid is hidden by design on paid rows. */}
            {paymentsStatus === "paid" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-700">
                Showing only paid transactions. Switch the Status filter to <strong>Pending</strong> (or <strong>All Statuses</strong>) to see the green <strong>Mark Paid</strong> button on each unpaid row.
              </div>
            )}

            {/* Table */}
            {paymentsLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#2832C2] animate-spin" /></div>
            ) : (
              <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">User</th>
                        <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Product</th>
                        <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Market</th>
                        <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Amount</th>
                        <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Status</th>
                        <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Stripe Session</th>
                        <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Date</th>
                        <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {payments.length === 0 ? (
                        <tr><td colSpan={8} className="text-center py-12 text-gray-400 text-sm">No transactions found</td></tr>
                      ) : payments.map((t, i) => (
                        <tr key={t.id || i} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-sm font-medium text-[#0A1128]">{t.user_name || (t.user_email ? t.user_email.split("@")[0] : "—")}</p>
                              <p className="text-xs text-gray-400">{t.user_email || t.guest_email || (t.user_id ? t.user_id.slice(0, 12) + "…" : "Guest (no email)")}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`border-0 text-xs ${
                              t.product === "season_pass" ? "bg-purple-100 text-purple-700" :
                              t.product === "resume_analyse" ? "bg-[#FF6B35]/10 text-[#FF6B35]" :
                              t.product === "ai_credits" ? "bg-blue-100 text-blue-700" :
                              "bg-gray-100 text-gray-600"
                            }`}>
                              {t.product || "—"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <MarketChip code={txnMarket(t)} />
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-['JetBrains_Mono'] text-sm font-semibold text-[#16a34a]">
                              {fmtTxnAmount(t)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`border-0 text-xs ${
                              t.payment_status === "paid" ? "bg-green-100 text-green-700" :
                              t.payment_status === "pending" ? "bg-yellow-100 text-yellow-700" :
                              "bg-red-100 text-red-700"
                            }`}>
                              {t.payment_status === "paid" ? "✓ Paid" : t.payment_status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {t.session_id ? (
                              <span className="font-['JetBrains_Mono'] text-xs text-gray-400 truncate max-w-[120px] block" title={t.session_id}>
                                {t.session_id.slice(0, 16)}...
                              </span>
                            ) : <span className="text-gray-300 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-500">{t.created_at?.slice(0, 10) || "—"}</span>
                          </td>
                          <td className="px-4 py-3">
                            {t.payment_status === "paid" ? (
                              <span className="text-xs text-gray-300">—</span>
                            ) : (
                              <button
                                onClick={() => markPaymentPaid(t)}
                                disabled={markingPaidId === t.id}
                                className="text-xs font-semibold px-3 py-1.5 rounded bg-[#16a34a] text-white hover:bg-[#15803d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors border-0 cursor-pointer"
                                data-testid={`mark-paid-${t.id}`}
                                title="Mark this payment as paid + create user + email forgot-password link"
                              >
                                {markingPaidId === t.id ? (
                                  <span className="flex items-center gap-1.5">
                                    <Loader2 className="w-3 h-3 animate-spin" /> Marking…
                                  </span>
                                ) : (
                                  "Mark Paid"
                                )}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {paymentsPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                    <span className="text-xs text-gray-500">Page {paymentsPage} of {paymentsPages}</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPaymentsPage(p => Math.max(1, p - 1))}
                        disabled={paymentsPage === 1}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => setPaymentsPage(p => Math.min(paymentsPages, p + 1))}
                        disabled={paymentsPage === paymentsPages}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 transition-colors"
                      >
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>
        )}

        {tab === "transactions" && (
          <div className="space-y-2">
            {recent_transactions?.map((t, i) => (
              <Card key={i} className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
                <CardContent className="pt-4 pb-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <Badge className={`border-0 text-xs ${t.product === 'season_pass' ? 'bg-[#2832C2]/20 text-[#2832C2]' : t.product === 'ai_credits' ? 'bg-[#2832C2]/20 text-[#2832C2]' : 'bg-[#2832C2]/20 text-[#2832C2]'}`}>
                      {t.product || "resume_analyse"}
                    </Badge>
                    <span className="text-gray-500 text-xs">{t.user_id?.slice(0, 8)}...</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`border-0 text-xs ${t.payment_status === 'paid' ? 'bg-[#16a34a]/20 text-[#16a34a]' : 'bg-[#eab308]/20 text-[#eab308]'}`}>
                      {t.payment_status}
                    </Badge>
                    <span className="font-['JetBrains_Mono'] text-[#0A1128] text-sm">{fmtTxnAmount(t)}</span>
                    <span className="text-gray-500 text-xs">{t.created_at?.slice(0, 10)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {tab === "ai-costs" && (
          <>
            {!aiCosts ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#2832C2] animate-spin" /></div>
            ) : (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard icon={Cpu} label="Total AI Spend" value={aiCosts.summary.total_cost_usd} color="#ef4444" prefix="$" />
                  <StatCard icon={Activity} label="Total API Calls" value={aiCosts.summary.total_api_calls} color="#2832C2" />
                  <StatCard icon={Zap} label="Total Tokens" value={aiCosts.summary.total_tokens?.toLocaleString()} color="#2832C2" />
                  <StatCard icon={DollarSign} label="Avg Cost/Call" value={aiCosts.summary.avg_cost_per_call?.toFixed(4)} color="#16a34a" prefix="$" />
                </div>

                {/* By Model & Endpoint */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]" data-testid="cost-by-model">
                    <CardHeader><CardTitle className="text-[#0A1128] text-sm font-['Chivo']">Cost by Model</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {Object.entries(aiCosts.by_model || {}).map(([model, data]) => (
                        <div key={model} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-100">
                          <div>
                            <span className="text-[#0A1128] text-sm font-mono">{model}</span>
                            <span className="text-gray-500 text-xs ml-2">{data.calls} calls</span>
                          </div>
                          <div className="text-right">
                            <span className="font-['JetBrains_Mono'] text-[#ef4444] text-sm">${data.cost.toFixed(4)}</span>
                            <span className="text-gray-500 text-xs ml-2">{data.tokens.toLocaleString()} tok</span>
                          </div>
                        </div>
                      ))}
                      {Object.keys(aiCosts.by_model || {}).length === 0 && (
                        <p className="text-gray-500 text-sm text-center py-4">No AI costs recorded yet</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]" data-testid="cost-by-endpoint">
                    <CardHeader><CardTitle className="text-[#0A1128] text-sm font-['Chivo']">Cost by Feature</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {Object.entries(aiCosts.by_endpoint || {}).map(([ep, data]) => (
                        <div key={ep} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-100">
                          <div>
                            <span className="text-[#0A1128] text-sm">{ep}</span>
                            <span className="text-gray-500 text-xs ml-2">{data.calls} calls</span>
                          </div>
                          <span className="font-['JetBrains_Mono'] text-[#ef4444] text-sm">${data.cost.toFixed(4)}</span>
                        </div>
                      ))}
                      {Object.keys(aiCosts.by_endpoint || {}).length === 0 && (
                        <p className="text-gray-500 text-sm text-center py-4">No AI costs recorded yet</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Per User */}
                <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]" data-testid="cost-by-user">
                  <CardHeader><CardTitle className="text-[#0A1128] text-sm font-['Chivo']">Top Users by AI Spend</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {(aiCosts.top_users || []).map((u, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-100">
                          <div>
                            <span className="text-[#0A1128] text-sm">{u.name || u.email}</span>
                            <span className="text-gray-500 text-xs ml-2">{u.email}</span>
                          </div>
                          <div className="flex items-center gap-4 text-xs">
                            <span className="text-gray-500">{u.calls} calls</span>
                            <span className="text-gray-500">{u.tokens.toLocaleString()} tok</span>
                            <span className="font-['JetBrains_Mono'] text-[#ef4444] text-sm">${u.cost.toFixed(4)}</span>
                          </div>
                        </div>
                      ))}
                      {(aiCosts.top_users || []).length === 0 && (
                        <p className="text-gray-500 text-sm text-center py-4">No AI usage data yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Costs */}
                <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]" data-testid="recent-ai-costs">
                  <CardHeader><CardTitle className="text-[#0A1128] text-sm font-['Chivo']">Recent API Calls</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {(aiCosts.recent_costs || []).map((c, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-100 text-xs">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-gray-100 text-gray-500 border-0 text-[10px]">{c.model}</Badge>
                            <span className="text-[#0A1128]">{c.endpoint}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-gray-500">{c.total_tokens} tok</span>
                            <span className="font-['JetBrains_Mono'] text-[#ef4444]">${c.estimated_cost_usd?.toFixed(5)}</span>
                            <span className="text-gray-500">{c.created_at?.slice(0, 10)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}

        {tab === "skill-paths" && (
          <>
            {!skillPathStats ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#2832C2] animate-spin" /></div>
            ) : (
              <div className="space-y-6">
                {/* Skill Path Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard icon={Route} label="Total Generations" value={skillPathStats.summary.total_generations} color="#8839DF" />
                  <StatCard icon={Cpu} label="AI Cost (USD)" value={skillPathStats.summary.total_ai_cost_usd?.toFixed(4)} color="#ef4444" prefix="$" />
                  <StatCard icon={Zap} label="Credits Used" value={skillPathStats.summary.total_credits_used} color="#F87533" />
                  <StatCard icon={Activity} label="Total Tokens" value={skillPathStats.summary.total_tokens?.toLocaleString()} color="#2832C2" />
                </div>

                {/* Averages */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard icon={DollarSign} label="Avg Cost/Generation" value={skillPathStats.summary.avg_cost_per_generation?.toFixed(6)} color="#16a34a" prefix="$" />
                  <StatCard icon={Activity} label="Avg Tokens/Generation" value={skillPathStats.summary.avg_tokens_per_generation?.toLocaleString()} color="#2832C2" />
                  <StatCard icon={FileText} label="Prompt Tokens" value={skillPathStats.summary.total_prompt_tokens?.toLocaleString()} color="#2832C2" />
                  <StatCard icon={FileText} label="Completion Tokens" value={skillPathStats.summary.total_completion_tokens?.toLocaleString()} color="#8839DF" />
                </div>

                {/* Model Breakdown & Top Users */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]" data-testid="skillpath-by-model">
                    <CardHeader><CardTitle className="text-[#0A1128] text-sm font-['Chivo']">Cost by Model</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {Object.entries(skillPathStats.by_model || {}).map(([model, data]) => (
                        <div key={model} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-100">
                          <div>
                            <span className="text-[#0A1128] text-sm font-mono">{model}</span>
                            <span className="text-gray-500 text-xs ml-2">{data.calls} calls</span>
                          </div>
                          <div className="text-right">
                            <span className="font-['JetBrains_Mono'] text-[#ef4444] text-sm">${data.cost.toFixed(4)}</span>
                            <span className="text-gray-500 text-xs ml-2">{data.tokens.toLocaleString()} tok</span>
                          </div>
                        </div>
                      ))}
                      {Object.keys(skillPathStats.by_model || {}).length === 0 && (
                        <p className="text-gray-500 text-sm text-center py-4">No skill path AI costs recorded yet</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]" data-testid="skillpath-top-users">
                    <CardHeader><CardTitle className="text-[#0A1128] text-sm font-['Chivo']">Top Users by Skill Path Usage</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {(skillPathStats.top_users || []).map((u, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-100">
                          <div>
                            <span className="text-[#0A1128] text-sm">{u.name || u.email}</span>
                            <span className="text-gray-500 text-xs ml-2">{u.email}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="text-gray-500">{u.generations} paths</span>
                            <span className="text-[#F87533] font-medium">{u.credits_used} credits</span>
                            <span className="font-['JetBrains_Mono'] text-[#ef4444] text-sm">${u.cost.toFixed(4)}</span>
                          </div>
                        </div>
                      ))}
                      {(skillPathStats.top_users || []).length === 0 && (
                        <p className="text-gray-500 text-sm text-center py-4">No skill path usage yet</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Skill Path Generations */}
                <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]" data-testid="recent-skillpath-generations">
                  <CardHeader><CardTitle className="text-[#0A1128] text-sm font-['Chivo']">Recent Skill Path Generations</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {(skillPathStats.recent_generations || []).map((g, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-100 text-xs">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#8839DF]/10">
                              <Target className="w-4 h-4 text-[#8839DF]" />
                            </div>
                            <div>
                              <p className="text-[#0A1128] text-sm font-medium">{g.target_role || "N/A"}</p>
                              <p className="text-gray-500 text-xs">{g.user_email} &middot; {g.experience_level || "N/A"}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-right">
                            <Badge className="bg-gray-100 text-gray-500 border-0 text-[10px]">{g.model}</Badge>
                            <span className="text-gray-500">{g.tokens_used?.toLocaleString()} tok</span>
                            <span className="font-['JetBrains_Mono'] text-[#ef4444]">${g.ai_cost_usd?.toFixed(5)}</span>
                            <span className="text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{g.created_at?.slice(0, 10)}</span>
                          </div>
                        </div>
                      ))}
                      {(skillPathStats.recent_generations || []).length === 0 && (
                        <p className="text-gray-500 text-sm text-center py-8">No skill path generations yet. Users need Full Career OS + AI Credits to generate paths.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}

        {/* Contact Submissions Tab */}
        {tab === "contact" && (
          <div className="space-y-6">
            {/* Header with filters */}
            <div className="flex items-center justify-between">
              <h2 className="text-[#0A1128] font-['Chivo'] text-lg">Contact Form Submissions ({contactTotal})</h2>
              <select
                value={contactFilter}
                onChange={(e) => { setContactFilter(e.target.value); setContactPage(1); }}
                className="px-4 py-2 border-2 border-[#e5e7eb] rounded-lg text-sm focus:border-[#2832C2] outline-none"
              >
                <option value="all">All Submissions</option>
                <option value="new">New</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            {/* Submissions List */}
            <div className="space-y-4">
              {contactSubmissions.map((submission) => (
                <Card key={submission.id} className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-[#0A1128] font-semibold">
                            {submission.first_name} {submission.last_name}
                          </h3>
                          <Badge className={`text-xs ${
                            submission.status === 'new' ? 'bg-blue-100 text-blue-800' :
                            submission.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          } border-0`}>
                            {submission.status.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Email:</strong> <a href={`mailto:${submission.email}`} className="text-[#2832C2] hover:underline">{submission.email}</a>
                        </p>
                        <p className="text-sm text-gray-600 mb-3">
                          <strong>Subject:</strong> {submission.subject.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                          {submission.message}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-xs text-gray-400">
                        Submitted: {new Date(submission.created_at).toLocaleString()}
                      </span>
                      <div className="flex gap-2">
                        {submission.status === 'new' && (
                          <button
                            onClick={() => updateContactStatus(submission.id, 'in_progress')}
                            className="px-3 py-1 text-xs bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200 border-0 cursor-pointer"
                          >
                            Mark In Progress
                          </button>
                        )}
                        {submission.status !== 'resolved' && (
                          <button
                            onClick={() => updateContactStatus(submission.id, 'resolved')}
                            className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200 border-0 cursor-pointer"
                          >
                            Mark Resolved
                          </button>
                        )}
                        {submission.status === 'resolved' && (
                          <button
                            onClick={() => updateContactStatus(submission.id, 'new')}
                            className="px-3 py-1 text-xs bg-gray-100 text-gray-800 rounded hover:bg-gray-200 border-0 cursor-pointer"
                          >
                            Reopen
                          </button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {contactSubmissions.length === 0 && (
                <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
                  <CardContent className="py-12 text-center">
                    <p className="text-gray-500">No contact form submissions yet</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Pagination */}
            {contactPages > 1 && (
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => setContactPage(p => Math.max(1, p - 1))}
                  disabled={contactPage === 1}
                  className="p-2 rounded border-2 border-[#e5e7eb] hover:border-[#2832C2] disabled:opacity-30 disabled:cursor-not-allowed bg-transparent cursor-pointer"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-500">Page {contactPage} of {contactPages}</span>
                <button
                  onClick={() => setContactPage(p => Math.min(contactPages, p + 1))}
                  disabled={contactPage === contactPages}
                  className="p-2 rounded border-2 border-[#e5e7eb] hover:border-[#2832C2] disabled:opacity-30 disabled:cursor-not-allowed bg-transparent cursor-pointer"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}


        {/* Resumes Tab */}
        {tab === "resumes" && (
          <div className="space-y-4">
            <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#2832C2]" />
                  Uploaded Resumes ({resumeTotal})
                </CardTitle>
                <div className="flex gap-3 mt-3 items-center">
                  <Input
                    placeholder="Search by filename, email, or phone..."
                    value={resumeSearch}
                    onChange={(e) => {
                      setResumeSearch(e.target.value);
                      setResumePage(1);
                    }}
                    className="max-w-sm"
                  />
                  <button
                    onClick={syncResumeContacts}
                    disabled={syncing}
                    className="flex items-center gap-2 bg-[#2832C2] text-white px-4 py-2 rounded-lg hover:bg-[#1e28a0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    data-testid="sync-resumes-btn"
                  >
                    {syncing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Activity className="w-4 h-4" />
                        Sync Contacts
                      </>
                    )}
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                {resumes.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No resumes found</p>
                ) : (
                  <div className="space-y-3">
                    {resumes.map((resume, idx) => (
                      <div key={resume.id || idx} className="p-4 border border-[#e5e7eb] rounded-lg hover:border-[#2832C2] transition-colors relative">
                        {resume.contacts_synced_at && (
                          <div className="absolute top-2 right-2">
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              ✓ Synced
                            </Badge>
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="w-4 h-4 text-gray-400" />
                              <span className="font-semibold text-sm text-[#0A1128]">{resume.filename || "Unnamed Resume"}</span>
                            </div>
                            <div className="text-xs text-gray-500 space-y-1">
                              <div><strong>Uploaded:</strong> {resume.uploaded_at ? new Date(resume.uploaded_at).toLocaleString() : "N/A"}</div>
                              <div><strong>User:</strong> {resume.user_name || "N/A"} ({resume.user_email || "N/A"})</div>
                              {resume.user_target_role && (
                                <div><strong>Target Role:</strong> {resume.user_target_role}</div>
                              )}
                              {resume.contacts_synced_at && (
                                <div className="text-green-600"><strong>Contacts Synced:</strong> {new Date(resume.contacts_synced_at).toLocaleString()}</div>
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs space-y-2">
                              {resume.extracted_emails && resume.extracted_emails.length > 0 && (
                                <div className="bg-blue-50 p-2 rounded">
                                  <div className="font-semibold text-gray-700 mb-1">📧 Extracted Emails:</div>
                                  {resume.extracted_emails.map((email, i) => (
                                    <div key={i} className="text-blue-600 font-mono">{email}</div>
                                  ))}
                                </div>
                              )}
                              {resume.extracted_phones && resume.extracted_phones.length > 0 && (
                                <div className="bg-green-50 p-2 rounded">
                                  <div className="font-semibold text-gray-700 mb-1">📞 Extracted Phones:</div>
                                  {resume.extracted_phones.map((phone, i) => (
                                    <div key={i} className="text-green-600 font-mono">{phone}</div>
                                  ))}
                                </div>
                              )}
                              {(!resume.extracted_emails || resume.extracted_emails.length === 0) && 
                               (!resume.extracted_phones || resume.extracted_phones.length === 0) && (
                                <div className="text-gray-400 italic">No contact details found</div>
                              )}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <button
                                onClick={() => downloadResumeText(resume.id, resume.filename)}
                                className="text-xs bg-[#2832C2] text-white px-3 py-1.5 rounded hover:bg-[#1e28a0] transition-colors"
                              >
                                Download Full Text
                              </button>
                              {resume.has_original_file && (
                                <button
                                  onClick={() => downloadOriginalResume(resume.id, resume.filename)}
                                  className="inline-flex items-center gap-1.5 text-xs border border-[#cfd5ff] text-[#2832C2] bg-[#f7f8ff] px-3 py-1.5 rounded hover:bg-[#eef0ff] transition-colors"
                                  data-testid={`download-original-resume-${resume.id}`}
                                >
                                  <Download className="w-3.5 h-3.5" />
                                  Download Resume
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        {resume.text_preview && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="text-xs text-gray-500 font-semibold mb-1">Preview:</div>
                            <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded font-mono whitespace-pre-wrap line-clamp-3">
                              {resume.text_preview}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pagination */}
            {resumePages > 1 && (
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setResumePage(p => Math.max(1, p - 1))}
                  disabled={resumePage === 1}
                  className="p-2 rounded border-2 border-[#e5e7eb] hover:border-[#2832C2] disabled:opacity-30 disabled:cursor-not-allowed bg-transparent cursor-pointer"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-500">
                  Page {resumePage} of {resumePages}
                </span>
                <button
                  onClick={() => setResumePage(p => Math.min(resumePages, p + 1))}
                  disabled={resumePage === resumePages}
                  className="p-2 rounded border-2 border-[#e5e7eb] hover:border-[#2832C2] disabled:opacity-30 disabled:cursor-not-allowed bg-transparent cursor-pointer"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── EMAIL LOGS TAB ──────────────────────────────────────────── */}
        {tab === "emails" && (
          <div className="space-y-4">

            {/* ── Test Email Card ── */}
            <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm text-[#0A1128]">
                  <Mail className="w-4 h-4 text-[#FF6B35]" /> Test SMTP Configuration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-gray-500 mb-3">Send a test email to verify your SMTP settings are working correctly.</p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="recipient@example.com"
                    value={testEmailAddr}
                    onChange={(e) => setTestEmailAddr(e.target.value)}
                    className="flex-1 text-gray-900 border-2 border-[#e5e7eb]"
                    data-testid="test-email-input"
                  />
                  <button
                    onClick={handleTestEmail}
                    disabled={testEmailSending}
                    className="px-4 py-2 bg-[#FF6B35] text-white rounded-lg text-sm font-semibold hover:bg-[#e55a2b] disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
                    data-testid="test-email-btn"
                  >
                    {testEmailSending ? <><Loader2 className="w-4 h-4 animate-spin" />Sending...</> : <><Mail className="w-4 h-4" />Send Test</>}
                  </button>
                </div>
                {testEmailResult && (
                  <div className={`mt-3 p-3 rounded-lg text-sm ${testEmailResult.success ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}
                    data-testid="test-email-result">
                    {testEmailResult.success ? "✓ " : "✗ "}{testEmailResult.message}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Summary stat cards */}
            {emailLogsSummary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Mail}         label="Total Sent"      value={emailLogsSummary.total_sent}   color="#22c55e" />
                <StatCard icon={XCircle}      label="Total Failed"    value={emailLogsSummary.total_failed} color="#ef4444" />
                <StatCard icon={Bell}         label="Nudge Emails"    value={(emailLogsSummary.by_type?.nudge?.sent || 0)} color="#2832C2" />
                <StatCard icon={CheckCircle}  label="Report Emails"   value={(emailLogsSummary.by_type?.report?.sent || 0)} color="#f59e0b" />
              </div>
            )}

            {/* Type breakdown */}
            {emailLogsSummary?.by_type && Object.keys(emailLogsSummary.by_type).length > 0 && (
              <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Activity className="w-4 h-4 text-[#2832C2]" /> Breakdown by Email Type
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(emailLogsSummary.by_type).map(([type, counts]) => (
                      <div key={type} className="p-3 border border-[#e5e7eb] rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-[#0A1128] capitalize">{type.replace(/_/g, " ")}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            type === "nudge" ? "bg-blue-100 text-blue-700" :
                            type === "report" ? "bg-yellow-100 text-yellow-700" :
                            type === "season_pass" ? "bg-purple-100 text-purple-700" :
                            type === "order_confirmation" ? "bg-green-100 text-green-700" :
                            "bg-gray-100 text-gray-600"
                          }`}>{type}</span>
                        </div>
                        <div className="flex gap-3 text-xs">
                          <span className="text-green-600 font-mono">✓ {counts.sent || 0} sent</span>
                          {counts.failed > 0 && <span className="text-red-500 font-mono">✗ {counts.failed} failed</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Logs table card */}
            <Card className="bg-white border-2 border-[#e5e7eb] rounded-[17.5px]">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#2832C2]" />
                    Email Logs ({emailLogsTotal})
                  </CardTitle>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={fetchEmailLogs}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <RefreshCw className="w-3 h-3" /> Refresh
                    </button>
                    <button
                      onClick={triggerNudgeEmails}
                      disabled={triggeringNudge}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs bg-[#2832C2] text-white rounded-lg hover:bg-[#1e28a0] transition-colors disabled:opacity-50"
                    >
                      {triggeringNudge ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
                      Trigger Nudge Job
                    </button>
                  </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-2 mt-3">
                  <Input
                    placeholder="Search by email..."
                    value={emailLogsSearch}
                    onChange={(e) => { setEmailLogsSearch(e.target.value); setEmailLogsPage(1); }}
                    className="max-w-xs text-sm"
                  />
                  <select
                    value={emailLogsType}
                    onChange={(e) => { setEmailLogsType(e.target.value); setEmailLogsPage(1); }}
                    className="border border-gray-200 rounded-md px-3 py-2 text-sm text-[#0A1128] bg-white focus:outline-none focus:border-[#2832C2]"
                  >
                    <option value="">All Types</option>
                    <option value="nudge">Nudge</option>
                    <option value="report">Report</option>
                    <option value="season_pass">Season Pass</option>
                    <option value="order_confirmation">Order Confirmation</option>
                    <option value="password_reset">Password Reset</option>
                  </select>
                  <select
                    value={emailLogsStatus}
                    onChange={(e) => { setEmailLogsStatus(e.target.value); setEmailLogsPage(1); }}
                    className="border border-gray-200 rounded-md px-3 py-2 text-sm text-[#0A1128] bg-white focus:outline-none focus:border-[#2832C2]"
                  >
                    <option value="">All Statuses</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                    <option value="skipped">Skipped</option>
                  </select>
                </div>
              </CardHeader>

              <CardContent>
                {emailLogs.length === 0 ? (
                  <div className="text-center py-12">
                    <Mail className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">No email logs found</p>
                    <p className="text-gray-300 text-xs mt-1">Logs appear here as emails are sent</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#e5e7eb]">
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Recipient</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Subject</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Sent At</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#f3f4f6]">
                        {emailLogs.map((log, idx) => (
                          <tr key={log.id || idx} className="hover:bg-gray-50 transition-colors">
                            <td className="py-3 px-3">
                              <span className="font-mono text-xs text-[#0A1128] font-medium">{log.to_email}</span>
                            </td>
                            <td className="py-3 px-3">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${
                                log.email_type === "nudge"              ? "bg-blue-100 text-blue-700" :
                                log.email_type === "report"             ? "bg-yellow-100 text-yellow-700" :
                                log.email_type === "season_pass"        ? "bg-purple-100 text-purple-700" :
                                log.email_type === "order_confirmation" ? "bg-green-100 text-green-700" :
                                log.email_type === "password_reset"     ? "bg-orange-100 text-orange-700" :
                                "bg-gray-100 text-gray-600"
                              }`}>
                                {log.email_type?.replace(/_/g, " ")}
                              </span>
                            </td>
                            <td className="py-3 px-3 max-w-[220px]">
                              <span className="text-xs text-gray-600 truncate block" title={log.subject}>{log.subject}</span>
                            </td>
                            <td className="py-3 px-3">
                              {log.status === "sent" && (
                                <span className="flex items-center gap-1 text-xs text-green-600 font-medium whitespace-nowrap">
                                  <CheckCircle className="w-3.5 h-3.5" /> Sent
                                </span>
                              )}
                              {log.status === "failed" && (
                                <span className="flex items-center gap-1 text-xs text-red-500 font-medium whitespace-nowrap">
                                  <XCircle className="w-3.5 h-3.5" /> Failed
                                </span>
                              )}
                              {log.status === "skipped" && (
                                <span className="flex items-center gap-1 text-xs text-gray-400 font-medium whitespace-nowrap">
                                  <AlertCircle className="w-3.5 h-3.5" /> Skipped
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-3 whitespace-nowrap">
                              <span className="text-xs text-gray-500">
                                {log.sent_at ? new Date(log.sent_at).toLocaleString("en-IN", {
                                  day: "2-digit", month: "short", year: "numeric",
                                  hour: "2-digit", minute: "2-digit"
                                }) : "—"}
                              </span>
                            </td>
                            <td className="py-3 px-3">
                              {log.status === "failed" && log.error && (
                                <span className="text-xs text-red-400 font-mono block max-w-[180px] truncate" title={log.error}>
                                  {log.error}
                                </span>
                              )}
                              {log.metadata?.detected_role && (
                                <span className="text-xs text-gray-400 block">Role: {log.metadata.detected_role}</span>
                              )}
                              {log.metadata?.score !== undefined && (
                                <span className="text-xs text-gray-400 block">Score: {log.metadata.score}</span>
                              )}
                              {log.metadata?.product && (
                                <span className="text-xs text-gray-400 block">{log.metadata.product}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pagination */}
            {emailLogsPages > 1 && (
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setEmailLogsPage(p => Math.max(1, p - 1))}
                  disabled={emailLogsPage === 1}
                  className="p-2 rounded border-2 border-[#e5e7eb] hover:border-[#2832C2] disabled:opacity-30 disabled:cursor-not-allowed bg-transparent cursor-pointer"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-500">Page {emailLogsPage} of {emailLogsPages}</span>
                <button
                  onClick={() => setEmailLogsPage(p => Math.min(emailLogsPages, p + 1))}
                  disabled={emailLogsPage === emailLogsPages}
                  className="p-2 rounded border-2 border-[#e5e7eb] hover:border-[#2832C2] disabled:opacity-30 disabled:cursor-not-allowed bg-transparent cursor-pointer"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

          </div>
        )}


        {/* Email Campaigns Tab */}
        {tab === "email-campaigns" && (
          <div className="space-y-6">
            {/* Stats Cards */}
            {contactStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Total Contacts" value={contactStats.total_contacts} color="#2832C2" />
                <StatCard icon={Mail} label="Recent (7 days)" value={contactStats.recent_7_days} color="#16a34a" />
                <StatCard icon={CheckCircle} label="Season Pass" value={contactStats.status_breakdown?.season_pass_holder || 0} color="#f59e0b" />
                <StatCard icon={Target} label="Guests" value={contactStats.status_breakdown?.guest || 0} color="#6b7280" />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowComposer(true)}
                className="px-4 py-2 bg-[#2832C2] text-white rounded-lg hover:bg-[#1e2590] transition flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Compose Email
              </button>
              <button
                onClick={fetchCampaigns}
                className="px-4 py-2 border-2 border-[#e5e7eb] text-gray-700 rounded-lg hover:border-[#2832C2] hover:text-[#2832C2] transition"
              >
                View Campaigns
              </button>
              <button
                onClick={() => setShowTemplateEditor(true)}
                className="px-4 py-2 border-2 border-[#e5e7eb] text-gray-700 rounded-lg hover:border-[#2832C2] hover:text-[#2832C2] transition"
              >
                Manage Templates
              </button>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <Input
                placeholder="Search email or name..."
                value={contactsSearch}
                onChange={(e) => setContactsSearch(e.target.value)}
                className="border-2 border-[#e5e7eb] rounded-lg text-gray-900"
              />
              <select
                value={contactsRole}
                onChange={(e) => setContactsRole(e.target.value)}
                className="px-3 py-2 border-2 border-[#e5e7eb] rounded-lg text-gray-700 bg-white"
              >
                <option value="">All Roles</option>
                <option value="Digital Marketing Manager">Digital Marketing</option>
                <option value="Data Analyst">Data Analyst</option>
                <option value="EV Engineer">EV Engineer</option>
                <option value="Software Engineer">Software Engineer</option>
                <option value="Product Manager">Product Manager</option>
              </select>
              <select
                value={contactsSource}
                onChange={(e) => setContactsSource(e.target.value)}
                className="px-3 py-2 border-2 border-[#e5e7eb] rounded-lg text-gray-700 bg-white"
              >
                <option value="">All Sources</option>
                <option value="lp_upload">LP Upload</option>
                <option value="hero_form">Hero Form</option>
                <option value="exit_intent">Exit Intent</option>
                <option value="authenticated_upload">Auth Upload</option>
              </select>
              <select
                value={contactsStatus}
                onChange={(e) => setContactsStatus(e.target.value)}
                className="px-3 py-2 border-2 border-[#e5e7eb] rounded-lg text-gray-700 bg-white"
              >
                <option value="">All Status</option>
                <option value="guest">Guest</option>
                <option value="registered_user">Registered</option>
                <option value="season_pass_holder">Season Pass</option>
              </select>
              <button
                onClick={() => {
                  setContactsSearch("");
                  setContactsRole("");
                  setContactsSource("");
                  setContactsStatus("");
                }}
                className="px-4 py-2 border-2 border-[#e5e7eb] text-gray-700 rounded-lg hover:border-red-500 hover:text-red-500 transition"
              >
                Clear Filters
              </button>
            </div>

            {/* Selected Contacts Info */}
            {selectedContacts.length > 0 && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 flex items-center justify-between">
                <span className="text-sm font-medium text-blue-900">
                  {selectedContacts.length} contact(s) selected
                </span>
                <button
                  onClick={() => setSelectedContacts([])}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear Selection
                </button>
              </div>
            )}

            {/* Contacts Table */}
            <div className="bg-white border-2 border-[#e5e7eb] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-[#e5e7eb]">
                    <tr>
                      <th className="text-left p-3 text-xs font-semibold text-gray-600">
                        <input
                          type="checkbox"
                          checked={selectedContacts.length === contacts.length && contacts.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded"
                        />
                      </th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-600">Email</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-600">Name</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-600">Role</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-600">Source</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-600">Status</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-600">Collected</th>
                      <th className="text-left p-3 text-xs font-semibold text-gray-600">Emails Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((c, i) => (
                      <tr key={i} className="border-b border-[#e5e7eb] hover:bg-gray-50">
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedContacts.includes(c.id)}
                            onChange={() => toggleContactSelection(c.id)}
                            className="rounded"
                          />
                        </td>
                        <td className="p-3 text-sm text-[#0A1128]">{c.email}</td>
                        <td className="p-3 text-sm text-gray-600">{c.name || '-'}</td>
                        <td className="p-3 text-xs">
                          {c.detected_roles && c.detected_roles.length > 0 ? (
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                              {c.detected_roles[0]}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="p-3 text-xs">
                          {c.sources && c.sources.length > 0 && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                              {c.sources[0]}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-xs">
                          <Badge className={
                            c.status === 'season_pass_holder' ? 'bg-green-100 text-green-700' :
                            c.status === 'registered_user' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {c.status}
                          </Badge>
                        </td>
                        <td className="p-3 text-xs text-gray-500">
                          {new Date(c.collected_at).toLocaleDateString()}
                        </td>
                        <td className="p-3 text-xs font-['JetBrains_Mono'] text-center">
                          {c.email_sent_count || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="p-4 border-t-2 border-[#e5e7eb] flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {contacts.length} of {contactsTotal} contacts
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setContactsPage(Math.max(1, contactsPage - 1))}
                    disabled={contactsPage === 1}
                    className="p-2 rounded border-2 border-[#e5e7eb] hover:border-[#2832C2] disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm font-['JetBrains_Mono']">
                    Page {contactsPage} of {contactsPages}
                  </span>
                  <button
                    onClick={() => setContactsPage(Math.min(contactsPages, contactsPage + 1))}
                    disabled={contactsPage >= contactsPages}
                    className="p-2 rounded border-2 border-[#e5e7eb] hover:border-[#2832C2] disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Campaigns View Panel */}
            {campaigns.length > 0 && (
              <div className="bg-white border-2 border-[#e5e7eb] rounded-lg overflow-hidden">
                <div className="p-4 border-b-2 border-[#e5e7eb] flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-[#0A1128]">Campaign History ({campaignsTotal} total)</h3>
                  <button onClick={() => setCampaigns([])} className="text-xs text-gray-400 hover:text-gray-600">✕ Close</button>
                </div>
                <div className="divide-y divide-[#e5e7eb]">
                  {campaigns.map((c, i) => (
                    <div key={i} className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div>
                        <p className="text-sm font-medium text-[#0A1128]">{c.subject}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Sent by {c.created_by} · {c.total_recipients} recipients · {new Date(c.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-green-600 font-medium">✓ {c.sent_count} sent</span>
                        {c.failed_count > 0 && <span className="text-xs text-red-500">✗ {c.failed_count} failed</span>}
                        <span className={`text-xs px-2 py-1 rounded font-medium ${c.status === 'completed' ? 'bg-green-100 text-green-700' : c.status === 'scheduled' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                          {c.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Template Editor Modal */}
            {showTemplateEditor && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#0A1128]">Email Templates</h3>
                    <button onClick={() => setShowTemplateEditor(false)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
                  </div>
                  <div className="space-y-4">
                    {emailTemplates.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-8">No templates yet. Create your first template below.</p>
                    ) : (
                      <div className="divide-y divide-[#e5e7eb]">
                        {emailTemplates.map((t, i) => (
                          <div key={i} className="py-3 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-[#0A1128]">{t.name}</p>
                              <p className="text-xs text-gray-500">{t.category} · {t.subject}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  setSelectedTemplate(t.id);
                                  setEmailSubject(t.subject);
                                  setEmailBody(t.html_body);
                                  setShowTemplateEditor(false);
                                  setShowComposer(true);
                                }}
                                className="text-xs px-3 py-1 bg-[#2832C2] text-white rounded hover:bg-[#1e2590]"
                              >
                                Use
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Create new template */}
                    <div className="border-t-2 border-[#e5e7eb] pt-4">
                      <h4 className="text-sm font-semibold text-[#0A1128] mb-3">Create New Template</h4>
                      <NewTemplateForm
                        onCreated={async () => {
                          await fetchEmailTemplates();
                        }}
                        getHeaders={getHeaders}
                        API={API}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Email Composer Modal */}
            {showComposer && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#0A1128]">Compose Bulk Email</h3>
                    <button onClick={() => setShowComposer(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Template (Optional)</label>
                      <select
                        value={selectedTemplate}
                        onChange={(e) => {
                          setSelectedTemplate(e.target.value);
                          const template = emailTemplates.find(t => t.id === e.target.value);
                          if (template) {
                            setEmailSubject(template.subject);
                            setEmailBody(template.html_body);
                          }
                        }}
                        className="w-full px-3 py-2 border-2 border-[#e5e7eb] rounded-lg text-gray-900 bg-white"
                      >
                        <option value="">Start from scratch</option>
                        {emailTemplates.map(t => (
                          <option key={t.id} value={t.id}>{t.name} ({t.category})</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                      <Input
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        placeholder="Enter email subject..."
                        className="border-2 border-[#e5e7eb] text-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Body (HTML)</label>
                      <textarea
                        value={emailBody}
                        onChange={(e) => setEmailBody(e.target.value)}
                        placeholder="Enter email body (HTML supported)..."
                        rows={12}
                        className="w-full px-3 py-2 border-2 border-[#e5e7eb] rounded-lg font-mono text-sm text-gray-900 bg-white"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Use {'{name}'} and {'{email}'} as placeholders
                      </p>
                    </div>

                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-900">
                        {selectedContacts.length > 0 
                          ? `Will send to ${selectedContacts.length} selected contact(s)`
                          : `Will send to all contacts matching current filters`
                        }
                      </p>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleSendBulkEmail}
                        disabled={sendingEmail}
                        className="flex-1 px-4 py-2 bg-[#2832C2] text-white rounded-lg hover:bg-[#1e2590] disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {sendingEmail ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4" />
                            Send Now
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setShowComposer(false)}
                        className="px-4 py-2 border-2 border-[#e5e7eb] text-gray-700 rounded-lg hover:border-red-500 hover:text-red-500"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}


      </div>
    </div>
    </AdminShell>
  );
}
